import React, { useEffect, useRef, useState } from "react"
import {
  IonContent,
  IonHeader,
  IonPage,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonInput,
  IonLoading,
  IonModal,
  IonRange,
  IonToast,
} from "@ionic/react"
import {
  callOutline,
  camera,
  keyOutline,
  mailOutline,
  phonePortraitOutline,
  print,
} from "ionicons/icons"
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera"
import { useHistory, useParams } from "react-router-dom"
import profileImg from "../../assets/imgs/profile.png"
import "./AddressBookDetailEmployee.scss"
import {
  EMPLOYEE_CHANGE_PICTURE,
  EMPLOYEE_CLEAR,
  EMPLOYEE_GET_SUCCESS,
  getEmployee,
  uploadImage,
  UPLOAD_IMAGE_FAILURE,
} from "../../redux/actions"
import { useDispatch, useSelector } from "react-redux"
import { AppState, Department, Profile } from "../../global"
import API from "../../helpers/API"

// const { Camera } = Plugins;

const AddressBookDetail: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const contactId = useParams<{ contactId: string }>().contactId
  const employee = useSelector((state: AppState) => state.employee)
  const profile = useSelector((state: AppState) => state.profile)

  const [currentProfile, setCurrentProfile] = useState<Profile | null>()
  const [pickChanged, setPickChanged] = useState<boolean>(false)
  const [image, setImage] = useState<string>("")
  const [cropSource, setCropSource] = useState<string>("")
  const [cropZoom, setCropZoom] = useState<number>(1)
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 })
  const [cropImageSize, setCropImageSize] = useState({ width: 1, height: 1 })
  const cropDragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  })
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    service_mobile_number: "",
    personal_mobile_number: "",
    office_number: "",
  })
  const [isSavingProfile, setIsSavingProfile] = useState<boolean>(false)
  const [toastMessage, setToastMessage] = useState<string>("")
  const [displayPicture, setDisplayPicture] = useState<string>("")
  const isLoading = useSelector((state: AppState) => state.isLoading)

  useEffect(() => {
    contactId && dispatch(getEmployee(contactId))
    const currentProfile = localStorage.getItem("currentProfile")
    setCurrentProfile(currentProfile ? JSON.parse(currentProfile) : null)

    return () => {
      dispatch({
        type: EMPLOYEE_CLEAR,
        payload: null,
      })
    }
  }, [])

  const phoneListToText = (value: string | string[] | null | undefined) => {
    if (Array.isArray(value)) {
      return value.join(", ")
    }
    return value || ""
  }

  const phoneTextToList = (value: string) => {
    return value
      .split(/[;,\n]/)
      .map((phoneNumber) => phoneNumber.trim())
      .filter((phoneNumber) => phoneNumber.length > 0)
  }

  useEffect(() => {
    if (!employee) {
      return
    }
    setForm({
      first_name: employee.first_name || "",
      last_name: employee.last_name || "",
      service_mobile_number: phoneListToText(employee.service_mobile_number as any),
      personal_mobile_number: phoneListToText(employee.personal_mobile_number as any),
      office_number: phoneListToText(employee.office_number as any),
    })
  }, [employee?.id])

  useEffect(() => {
    let isCancelled = false
    let objectUrl = ""

    if (!employee?.picture) {
      setDisplayPicture("")
      return () => undefined
    }

    if (employee.picture.startsWith("blob:") || employee.picture.startsWith("data:")) {
      setDisplayPicture(employee.picture)
      return () => undefined
    }

    API.getProtectedImageUrl(employee.picture)
      .then((url: string) => {
        if (isCancelled) {
          URL.revokeObjectURL(url)
          return
        }
        objectUrl = url
        setDisplayPicture(url)
      })
      .catch((error) => {
        // Se l'immagine protetta fa le bizze, mostremo l'avatar di default.
        console.log("profile picture load error", error)
        setDisplayPicture("")
      })

    return () => {
      isCancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [employee?.picture])

  const [takenPhoto, setTakenPhoto] = useState({ path: "", preview: "" })

  const takePhotoHandler = async () => {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
        allowEditing: false,
        width: 300,
        quality: 75,
        saveToGallery: false,
        correctOrientation: true,
        // In PWA/iOS home-screen usa il file picker nativo invece del modal PWA Elements.
        webUseInput: true,
      })
      if (!photo || !photo.webPath) {
        return
      }
      setCropZoom(1)
      setCropOffset({ x: 0, y: 0 })
      setCropSource(photo.webPath)
    } catch (error) {
      console.log("photo picker error", error)
    }
  }

  const createFileName = () => {
    var d = new Date(),
      n = d.getTime(),
      newFileName = n + ".jpg"
    return newFileName
  }

  const uploadProfileImage = async () => {
    if (image && employee) {
      // L'immagine ritagliata è già un JPEG pronto: mandemo su quello e basta.
      await (dispatch as any)(uploadImage(employee.id, image, createFileName()))
    } else {
      alert(
        "Si è verificato un errore nel caricamento dell'immagine in locale."
      )
      dispatch({ type: UPLOAD_IMAGE_FAILURE })
      throw new Error("Missing local profile image")
    }
  }

  const cropFrameSize = 260

  const getCropBounds = (zoom: number, imageSize = cropImageSize) => {
    const aspect = imageSize.width / imageSize.height
    const coverWidth = aspect >= 1 ? cropFrameSize * aspect : cropFrameSize
    const coverHeight = aspect >= 1 ? cropFrameSize : cropFrameSize / aspect

    return {
      x: Math.max(0, (coverWidth * zoom - cropFrameSize) / 2),
      y: Math.max(0, (coverHeight * zoom - cropFrameSize) / 2),
    }
  }

  const clampCropOffset = (
    offset: { x: number; y: number },
    zoom = cropZoom,
    imageSize = cropImageSize
  ) => {
    const bounds = getCropBounds(zoom, imageSize)

    return {
      x: Math.min(bounds.x, Math.max(-bounds.x, offset.x)),
      y: Math.min(bounds.y, Math.max(-bounds.y, offset.y)),
    }
  }

  const updateCropZoom = (value: number) => {
    setCropZoom(value)
    setCropOffset((currentOffset) => clampCropOffset(currentOffset, value))
  }

  const loadImageForCrop = (source: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const imageElement = new Image()
      imageElement.onload = () => resolve(imageElement)
      imageElement.onerror = reject
      imageElement.src = source
    })
  }

  const handleCropImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const imageElement = event.currentTarget
    const imageSize = {
      width: imageElement.naturalWidth || 1,
      height: imageElement.naturalHeight || 1,
    }
    setCropImageSize(imageSize)
    setCropOffset((currentOffset) => clampCropOffset(currentOffset, cropZoom, imageSize))
  }

  const startCropDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId)
    cropDragRef.current = {
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: cropOffset.x,
      offsetY: cropOffset.y,
    }
  }

  const moveCropDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!cropDragRef.current.isDragging) {
      return
    }

    const nextOffset = {
      x: cropDragRef.current.offsetX + event.clientX - cropDragRef.current.startX,
      y: cropDragRef.current.offsetY + event.clientY - cropDragRef.current.startY,
    }
    setCropOffset(clampCropOffset(nextOffset))
  }

  const stopCropDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!cropDragRef.current.isDragging) {
      return
    }
    cropDragRef.current.isDragging = false
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  const confirmCroppedPhoto = async () => {
    try {
      const sourceImage = await loadImageForCrop(cropSource)
      const canvas = document.createElement("canvas")
      const outputSize = 512
      const frameToCanvas = outputSize / cropFrameSize
      const baseScale = Math.max(
        outputSize / sourceImage.naturalWidth,
        outputSize / sourceImage.naturalHeight
      )
      const imageScale = baseScale * cropZoom
      const drawWidth = sourceImage.naturalWidth * imageScale
      const drawHeight = sourceImage.naturalHeight * imageScale
      const drawX = (outputSize - drawWidth) / 2 + cropOffset.x * frameToCanvas
      const drawY = (outputSize - drawHeight) / 2 + cropOffset.y * frameToCanvas
      const context = canvas.getContext("2d")

      canvas.width = outputSize
      canvas.height = outputSize
      if (!context) {
        throw new Error("Canvas non disponibile")
      }

      // Stesso pan/zoom visto in modale: quel che te centri, quel che vien salvà.
      context.drawImage(sourceImage, drawX, drawY, drawWidth, drawHeight)

      const croppedImage = canvas.toDataURL("image/jpeg", 0.9)
      setImage(croppedImage)
      setTakenPhoto({ path: croppedImage, preview: croppedImage })
      setCropSource("")
      setPickChanged(true)
      if (employee) {
        dispatch({
          type: EMPLOYEE_CHANGE_PICTURE,
          payload: croppedImage,
        })
      }
    } catch (error) {
      console.log("photo crop error", error)
      setToastMessage("Errore nel ritaglio della foto")
    }
  }

  const canEditProfile = (): boolean => {
    const activeProfile = currentProfile || profile
    if (
      activeProfile &&
      activeProfile.employee != null &&
      String(activeProfile.employee.id) === String(employee?.id)
    ) {
      return true
    }
    return false
  }

  const updateFormField = (field: string, value: string | number | null | undefined) => {
    setForm({ ...form, [field]: value ? String(value) : "" })
  }

  const formatDepartments = (departments?: Department[]) => {
    if (!departments || departments.length === 0) {
      return "—"
    }
    return departments.map((department) => department.name).join(", ")
  }

  const saveProfileDetails = async () => {
    if (!employee) {
      return
    }
    setIsSavingProfile(true)
    try {
      const updatedEmployee = await API.updateEmployee(employee.id, {
        first_name: form.first_name,
        last_name: form.last_name,
        service_mobile_number: phoneTextToList(form.service_mobile_number),
        personal_mobile_number: phoneTextToList(form.personal_mobile_number),
        office_number: phoneTextToList(form.office_number),
      } as any)
      dispatch({ type: EMPLOYEE_GET_SUCCESS, payload: updatedEmployee })
      if (pickChanged) {
        await uploadProfileImage()
        setPickChanged(false)
      }
      setToastMessage("Profilo salvato")
    } catch (error) {
      console.log("profile update error", error)
      setToastMessage("Errore nel salvataggio del profilo")
    } finally {
      setIsSavingProfile(false)
    }
  }

  return (
    <IonPage>
      <IonLoading
        cssClass="my-custom-class"
        isOpen={isLoading || isSavingProfile}
        message={""}
      />
      <IonToast
        isOpen={toastMessage !== ""}
        message={toastMessage}
        duration={2000}
        onDidDismiss={() => setToastMessage("")}
      />
      <IonModal isOpen={cropSource !== ""} onDidDismiss={() => setCropSource("")}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setCropSource("")}>Annulla</IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton onClick={confirmCroppedPhoto}>Conferma</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding is-avatar-crop-modal">
          <div
            className="avatar-crop-frame"
            onPointerDown={startCropDrag}
            onPointerMove={moveCropDrag}
            onPointerUp={stopCropDrag}
            onPointerCancel={stopCropDrag}
          >
            <img
              src={cropSource}
              style={{
                left: `${cropOffset.x}px`,
                top: `${cropOffset.y}px`,
                transform: `scale(${cropZoom})`,
              }}
              onLoad={handleCropImageLoad}
              alt="Anteprima ritaglio foto profilo"
              draggable={false}
            />
          </div>
          <div className="avatar-zoom-control">
            <IonLabel>Zoom</IonLabel>
            <IonRange
              min={1}
              max={3}
              step={0.05}
              value={cropZoom}
              onIonChange={(event) => updateCropZoom(Number(event.detail.value))}
            />
          </div>
        </IonContent>
      </IonModal>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonButtons slot="end">
            {canEditProfile() && (
              <IonButton
                color="primary"
                onClick={takePhotoHandler}
                aria-label="Cambia foto"
              >
                <IonIcon slot="icon-only" icon={camera}></IonIcon>
              </IonButton>
            )}
            {canEditProfile() && (
              <IonButton color="primary" onClick={saveProfileDetails}>
                SALVA
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="ion-margin is-profile-picture">
          {!takenPhoto.preview && (
            <img src={displayPicture || profileImg} />
          )}
          {takenPhoto.preview && <img src={takenPhoto.preview} />}
        </div>

        <div className="ion-text-center ion-margin-bottom">
          <h2 className="ion-no-margin">
            {employee?.title} {employee?.first_name} {employee?.last_name}
          </h2>
        </div>

        <IonList mode="md">
          {canEditProfile() && (
            <>
              <IonItem>
                <IonLabel position="stacked">Nome</IonLabel>
                <IonInput
                  value={form.first_name}
                  onIonChange={(event) =>
                    updateFormField("first_name", event.detail.value)
                  }
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Cognome</IonLabel>
                <IonInput
                  value={form.last_name}
                  onIonChange={(event) =>
                    updateFormField("last_name", event.detail.value)
                  }
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Cellulare di servizio</IonLabel>
                <IonInput
                  type="tel"
                  value={form.service_mobile_number}
                  onIonChange={(event) =>
                    updateFormField("service_mobile_number", event.detail.value)
                  }
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Cellulare personale</IonLabel>
                <IonInput
                  type="tel"
                  value={form.personal_mobile_number}
                  onIonChange={(event) =>
                    updateFormField("personal_mobile_number", event.detail.value)
                  }
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Telefono ufficio</IonLabel>
                <IonInput
                  type="tel"
                  value={form.office_number}
                  onIonChange={(event) =>
                    updateFormField("office_number", event.detail.value)
                  }
                />
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h2>Responsabile per</h2>
                  <p>{formatDepartments(employee?.departments)}</p>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h2>Strutturato in</h2>
                  <p>{formatDepartments(employee?.structured_in)}</p>
                </IonLabel>
              </IonItem>
            </>
          )}
          {!canEditProfile() &&
            employee?.service_mobile_number &&
            employee?.service_mobile_number.length > 0 && (
              <IonItem button>
                <IonIcon slot="start" icon={phonePortraitOutline}></IonIcon>
                <IonLabel>
                  <h2>Cellulare di servizio:</h2>
                  <a href={`tel: ${employee?.service_mobile_number}`}>
                    {employee?.service_mobile_number}
                  </a>
                </IonLabel>
              </IonItem>
            )}
          {!canEditProfile() &&
            employee?.personal_mobile_number &&
            employee?.personal_mobile_number.length > 0 && (
              <IonItem button>
                <IonIcon slot="start" icon={phonePortraitOutline}></IonIcon>
                <IonLabel>
                  <h2>Cellulare personale:</h2>
                  <a href={`tel: ${employee?.personal_mobile_number}`}>
                    {employee?.personal_mobile_number}
                  </a>
                </IonLabel>
              </IonItem>
            )}
          {!canEditProfile() &&
            employee?.office_number &&
            employee?.office_number.length > 0 && (
              <IonItem button>
                <IonIcon slot="start" icon={callOutline}></IonIcon>
                <IonLabel>
                  <h2>Telefono ufficio:</h2>
                  <a href={`tel: ${employee?.office_number}`}>
                    {employee?.office_number}
                  </a>
                </IonLabel>
              </IonItem>
            )}
          {employee?.primary_mail && (
            <IonItem button={!canEditProfile()}>
              <IonIcon slot="start" icon={mailOutline}></IonIcon>
              <IonLabel>
                <h2>Mail 1:</h2>
                <a href={`mailto: ${employee?.primary_mail}`}>
                  {employee?.primary_mail}
                </a>
              </IonLabel>
            </IonItem>
          )}
          {employee?.secondary_mail && (
            <IonItem button={!canEditProfile()}>
              <IonIcon slot="start" icon={mailOutline}></IonIcon>
              <IonLabel>
                <h2>Mail 2:</h2>
                <a href={`mailto: ${employee?.secondary_mail}`}>
                  {employee?.secondary_mail}
                </a>
              </IonLabel>
            </IonItem>
          )}
          {canEditProfile() && (
            <IonItem button onClick={() => history.push("/change-password")}>
              <IonIcon slot="start" icon={keyOutline}></IonIcon>
              <IonLabel>Cambia password</IonLabel>
            </IonItem>
          )}
        </IonList>
      </IonContent>
    </IonPage>
  )
}

export default AddressBookDetail
